import type { Edge, Node } from '@xyflow/react';
import type {
  InternalNetworkAssessmentActionDto,
  InternalNetworkAssessmentEntityActionGraphDto,
  InternalNetworkAssessmentEntityDto,
} from '@terra/shared-contracts-internal-network';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import type { AssessmentAttackGraph } from './assessment-attack-graph.types';
import type { AttackPathEventColumnTick } from './build-attack-path-event-graph.util';
import {
  appendUnreachedGoalGaps,
  resolveFrontierEntityId,
} from './build-assessment-entity-action-graph-gap.util';
import {
  LIVE_FRONTIER_FRAME_ID,
  focusLatestRunningCampaign,
  markNodeRunning,
  prependSignalExecutionFrame,
} from './build-assessment-entity-action-graph-frame.util';
import { resolveGoalInfraImpacts } from './resolve-assessment-entity-action-impact-paths.util';

const ENTITY_COLUMN_WIDTH = 300;
const ENTITY_ROW_HEIGHT = 150;

const resolveColumns = ({
  graph,
}: {
  graph: InternalNetworkAssessmentEntityActionGraphDto;
}): Map<string, number> => {
  const sourceActionByTarget = new Map(
    graph.actions.map((action) => [action.targetEntityId, action]),
  );
  const columnByEntityId = new Map<string, number>();

  const visit = (entityId: string, path: Set<string>): number => {
    const existing = columnByEntityId.get(entityId);

    if (existing !== undefined) {
      return existing;
    }

    if (path.has(entityId)) {
      return 0;
    }

    const sourceAction = sourceActionByTarget.get(entityId);
    const column = sourceAction
      ? visit(sourceAction.sourceEntityId, new Set([...path, entityId])) + 1
      : 0;

    columnByEntityId.set(entityId, column);

    return column;
  };

  graph.entities.forEach((entity) => visit(entity.id, new Set()));

  return columnByEntityId;
};

const resolveEntityEventKind = (
  entity: InternalNetworkAssessmentEntityDto,
): AttackPathEventNodeData['kind'] => {
  if (entity.kind === 'PRINCIPAL') {
    return 'CREDENTIAL_OBTAINED';
  }

  if (entity.kind === 'GOAL') {
    return 'IMPACT_ACHIEVED';
  }

  return 'HOST_DISCOVERED';
};

const resolveEntityAdKind = (
  entity: InternalNetworkAssessmentEntityDto,
): AttackPathEventNodeData['entityKind'] => {
  if (entity.kind === 'PRINCIPAL') {
    return entity.adEntityKind ?? 'USER';
  }

  if (entity.kind === 'GOAL') {
    return 'DOMAIN';
  }

  return entity.adEntityKind ?? 'COMPUTER';
};

export type BuildAssessmentEntityActionGraphOptions = {
  isLive?: boolean;
};

const resolveEntityCanvasState = (
  status: InternalNetworkAssessmentEntityDto['status'],
  isLive: boolean,
): Pick<
  AttackPathEventNodeData,
  'status' | 'chainStatus' | 'dimmed' | 'isCampaignRunning'
> => {
  if (status === 'FAILED') {
    return {
      status: 'FAILED',
      chainStatus: 'BLOCKED',
      dimmed: false,
      isCampaignRunning: false,
    };
  }

  if (status === 'SUCCEEDED' || status === 'OWNED') {
    return {
      status: 'SUCCEEDED',
      chainStatus: 'COMPLETED',
      dimmed: false,
      isCampaignRunning: false,
    };
  }

  if (status === 'IN_PROGRESS') {
    return {
      status: 'IN_PROGRESS',
      chainStatus: isLive ? 'IN_PROGRESS' : 'BLOCKED',
      dimmed: false,
      isCampaignRunning: isLive,
    };
  }

  return {
    status: 'SUCCEEDED',
    chainStatus: 'COMPLETED',
    dimmed: true,
    isCampaignRunning: false,
  };
};

const buildEntityNodeData = ({
  entity,
  column,
  inboundAction,
  isLive,
}: {
  entity: InternalNetworkAssessmentEntityDto;
  column: number;
  inboundAction?: InternalNetworkAssessmentActionDto;
  isLive: boolean;
}): AttackPathEventNodeData => ({
  role: 'ENTITY',
  campaignId: inboundAction?.signalId ?? null,
  campaignStatus: null,
  chainProgress: entity.status === 'SUCCEEDED' ? 1 : 0,
  kind: resolveEntityEventKind(entity),
  typeLabel: entity.kind,
  title: entity.label,
  subtitle: inboundAction?.technique ?? null,
  caption: null,
  weaknessId: inboundAction?.weaknessId ?? null,
  mitreTechniqueId: inboundAction?.mitreTechniqueId ?? null,
  provenance: null,
  privilegeTier: null,
  resourceKind: null,
  severity: null,
  ...resolveEntityCanvasState(entity.status, isLive),
  isProven: entity.status === 'OWNED' || entity.status === 'SUCCEEDED',
  exploitsWeakness: false,
  isCrownJewel: entity.kind === 'GOAL' && entity.status === 'SUCCEEDED',
  isDomainController: false,
  impacts: resolveGoalInfraImpacts(entity),
  elapsedSeconds: column,
  occurredAt: new Date(0).toISOString(),
  hopIndex: column,
  retryCount: 0,
  findingId: null,
  hostname: entity.hostname,
  entityKind: resolveEntityAdKind(entity),
  entityId: entity.adEntityId,
  objective: null,
  signalId: inboundAction?.signalId ?? null,
  primaryExecutionId: null,
  durationSeconds: null,
  column,
  lane: 0,
});

const buildActionEdge = ({
  action,
  isLive,
}: {
  action: InternalNetworkAssessmentActionDto;
  isLive: boolean;
}): Edge<AttackPathEventEdgeData> => ({
  id: action.id,
  source: action.sourceEntityId,
  target: action.targetEntityId,
  type: 'attackPathEventEdge',
  data: {
    label: action.technique ?? action.mitreTechniqueId ?? action.eventKind,
    variant: 'proven',
    campaignId: action.signalId,
    status: action.status,
    isCampaignRunning: isLive && action.status === 'IN_PROGRESS',
  },
});

const placeGoalEntities = ({
  graph,
  columnByEntityId,
}: {
  graph: InternalNetworkAssessmentEntityActionGraphDto;
  columnByEntityId: Map<string, number>;
}): void => {
  const maxNonGoalColumn = Math.max(
    0,
    ...graph.entities
      .filter((entity) => entity.kind !== 'GOAL')
      .map((entity) => columnByEntityId.get(entity.id) ?? 0),
  );

  graph.entities
    .filter((entity) => entity.kind === 'GOAL')
    .forEach((entity) => {
      const column = columnByEntityId.get(entity.id) ?? 0;

      if (column === 0) {
        columnByEntityId.set(entity.id, maxNonGoalColumn + 1);
      }
    });
};

export const buildAssessmentEntityActionGraph = (
  graph: InternalNetworkAssessmentEntityActionGraphDto,
  { isLive = !graph.completed }: BuildAssessmentEntityActionGraphOptions = {},
): AssessmentAttackGraph => {
  const columnByEntityId = resolveColumns({ graph });

  placeGoalEntities({ graph, columnByEntityId });
  const goalGaps = appendUnreachedGoalGaps({ graph, columnByEntityId });
  const inboundActionByTarget = new Map(
    graph.actions.map((action) => [action.targetEntityId, action]),
  );
  const entityIdsByColumn = new Map<number, string[]>();

  graph.entities.forEach((entity) => {
    const column = columnByEntityId.get(entity.id) ?? 0;
    const entityIds = entityIdsByColumn.get(column) ?? [];

    entityIds.push(entity.id);
    entityIdsByColumn.set(column, entityIds);
  });

  const nodes: Node<AttackPathEventNodeData>[] = graph.entities.map(
    (entity) => {
      const column = columnByEntityId.get(entity.id) ?? 0;
      const lane = entityIdsByColumn.get(column)?.indexOf(entity.id) ?? 0;

      return {
        id: entity.id,
        type: 'attackPathEventNode',
        position: {
          x: column * ENTITY_COLUMN_WIDTH,
          y: lane * ENTITY_ROW_HEIGHT,
        },
        data: {
          ...buildEntityNodeData({
            entity,
            column,
            inboundAction: inboundActionByTarget.get(entity.id),
            isLive,
          }),
          lane,
        },
      };
    },
  );
  const entityNodeById = new Map(nodes.map((node) => [node.id, node]));

  const frontierEntityId = resolveFrontierEntityId(graph);

  graph.signalExecutions.forEach((execution) => {
    const enclosed = execution.entityIds.flatMap((entityId) => {
      const node = entityNodeById.get(entityId);

      return node ? [node] : [];
    });
    const fallback =
      enclosed.length === 0 &&
      (execution.status === 'PENDING' || execution.status === 'RUNNING') &&
      frontierEntityId
        ? entityNodeById.get(frontierEntityId)
        : undefined;
    const framed = fallback ? [fallback] : enclosed;

    prependSignalExecutionFrame({
      nodes,
      framed,
      signalId: execution.signalId,
      primaryExecutionId: execution.primaryExecutionId,
      objective: execution.objective,
      status: execution.status,
      startedAt: execution.startedAt,
      isLive,
      selectable: Boolean(execution.signalId),
    });
  });

  const columns: AttackPathEventColumnTick[] = [
    ...new Set([...columnByEntityId.values()]),
  ]
    .sort((left, right) => left - right)
    .map((column) => ({
      column,
      x: column * ENTITY_COLUMN_WIDTH,
      elapsedSeconds: column,
      occurredAt: new Date(column * 1000).toISOString(),
    }));

  const assembled = [...goalGaps.nodes, ...nodes];
  const hasRunningFrame = assembled.some(
    (node) =>
      node.data.role === 'SIGNAL_EXECUTION' && node.data.isCampaignRunning,
  );

  if (isLive && !hasRunningFrame) {
    const liveNode =
      assembled.find((node) => node.id === frontierEntityId) ??
      assembled.find((node) => node.data.isCampaignRunning);

    if (liveNode && liveNode.data.role !== 'UNKNOWN_GAP') {
      markNodeRunning(liveNode);
      prependSignalExecutionFrame({
        nodes: assembled,
        framed: [liveNode],
        signalId: LIVE_FRONTIER_FRAME_ID,
        primaryExecutionId: null,
        objective: liveNode.data.title,
        status: 'RUNNING',
        startedAt: null,
        isLive: true,
        selectable: false,
      });
      const liveFrame = assembled[0];

      if (liveFrame) {
        liveFrame.data = {
          ...liveFrame.data,
          signalId: null,
        };
      }
    }
  }

  const edges = [
    ...graph.actions.map((action) => buildActionEdge({ action, isLive })),
    ...goalGaps.edges,
  ];

  if (isLive) {
    focusLatestRunningCampaign({ nodes: assembled, edges });
  }

  return {
    nodes: assembled,
    edges,
    columns,
    hasTiming: false,
    maxElapsedSeconds: 0,
    frames: [],
  };
};
